import { MAX_1_CHARACTERS_MESSAGE, REQUIRED_MESSAGE } from "@/utils/constant";
import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryClient,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import { Paginated } from "./types";
import { generateParams } from "@/utils/helper/generateParams";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { stringRange } from "@/utils/helper/refinement";

const buildingPayloadSchema = z.object({
  categoryBizpass: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(4, 50)),
  dwellingType: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .max(1, { message: MAX_1_CHARACTERS_MESSAGE }),
});

type BuildingPayloadSchema = z.infer<typeof buildingPayloadSchema>;

type BuildingData = {
  id: string;
  categoryBizpass: string;
  dwellingType: string;
};

/* ============= *
 * READ building *
 * ============= */

type BuildingsQueryKey = DataConfig & {
  categoryBizpass?: string;
  dwellingType?: string;
};
interface BuildingList extends Paginated {
  content: BuildingData[];
}

async function getBuildings(
  queryFunctionContext: QueryFunctionContext,
): Promise<BuildingList> {
  const { queryKey } = queryFunctionContext;
  const [_, buildingsQueryKey] = queryKey;
  const params = generateParams(buildingsQueryKey as BuildingsQueryKey);

  const response = await axiosInstance.get("/api/crud/building" + params);
  return response.data.data;
}

function useBuildings(
  buildingsQueryKey: BuildingsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["buildings", buildingsQueryKey],
    queryFn: getBuildings,
  });
}

interface BuildingQueryKey {
  id: string;
}

async function getBuilding(
  queryFunctionContext: QueryFunctionContext,
): Promise<BuildingData> {
  const { queryKey } = queryFunctionContext;
  const [_, buildingQueryKey] = queryKey;
  const { id } = buildingQueryKey as BuildingQueryKey;

  const response = await axiosInstance.get(`/api/crud/building/${id}`);
  return response.data.data;
}

function useBuilding(buildingQueryKey: BuildingQueryKey) {
  const { id } = buildingQueryKey;
  return useQuery({
    queryKey: ["building", { id }],
    queryFn: getBuilding,
  });
}

function buildingLoader(
  queryClient: QueryClient,
): LoaderFunction<BuildingData> {
  return async ({ params }: LoaderFunctionArgs<{ id: string }>) => {
    const building = await queryClient.ensureQueryData({
      queryKey: ["building", { id: params.id }],
      queryFn: getBuilding,
    });
    return building;
  };
}

/* =============== *
 * CREATE building *
 * =============== */

async function createBuilding(
  data: BuildingPayloadSchema,
): Promise<BuildingData> {
  const response = await axiosInstance.post("/api/crud/building", data);
  return response.data;
}

function useBuildingCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: BuildingPayloadSchema }) =>
      createBuilding(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buildings"] });
    },
  });
}

/* =============== *
 * UPDATE building *
 * =============== */

async function editBuilding(id: string, data: BuildingPayloadSchema) {
  const response = await axiosInstance.post(`/api/crud/building/${id}`, data);
  return response.data;
}

function useBuildingEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BuildingPayloadSchema }) =>
      editBuilding(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buildings"] });
    },
  });
}

/* =============== *
 * DELETE building *
 * =============== */

async function deleteBuilding(id: string) {
  const response = await axiosInstance.delete(`/api/crud/building/${id}`);
  return response.data;
}

function useBuildingDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteBuilding(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buildings"] });
    },
  });
}

async function deleteBulkBuilding(ids: string[]) {
  return await Promise.all(ids.map((id) => deleteBuilding(id)));
}

function useBuildingBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) => deleteBulkBuilding(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buildings"] });
    },
  });
}

/* =============== *
 * DOWNLOAD Building *
 * =============== */

async function waitForMediaCompletion(
  id: string,
  interval = 2000,
): Promise<{ status: string; path?: string; fileName?: string }> {
  let currentStatus = "";
  let path: string | undefined;
  let fileName: string | undefined;
  while (currentStatus !== "SUCCESS") {
    const res = await axiosInstance.get(`/api/crud/media/${id}`);
    currentStatus = res.data.data.status;
    path = res.data.data.path;
    fileName = res.data.data.originalFileName ?? "list-apd.xlsx";

    if (currentStatus === "ERROR") {
      return { status: "ERROR", path, fileName };
    }

    if (currentStatus !== "SUCCESS") {
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
  return { status: currentStatus, path, fileName };
}

async function exportBuilding(
  config: DataConfig = {},
): Promise<{ status: string; fileName?: string }> {
  const configCopy = { ...config };

  // Step 1: Create Excel
  const createResponse = await axiosInstance.get("/api/crud/building/excel", {
    params: configCopy,
  });

  // Step 2: Monitor Status
  const statusResponse = await waitForMediaCompletion(
    createResponse.data.data.id,
  );

  if (statusResponse.status === "SUCCESS") {
    // Step 3: Download File
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: {
          path: statusResponse.path,
          fileName: statusResponse.fileName,
        },
        responseType: "blob",
      },
    );

    const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
    const link = document.createElement("a");
    link.href = url;
    const filename: string | undefined = statusResponse.fileName;
    link.setAttribute("download", filename ?? "building-list.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  // Return the final status and file name
  return { status: statusResponse.status };
}
function useExportBuilding() {
  return useMutation({
    mutationFn: async ({ config }: { config?: DataConfig; name?: string }) => {
      return await exportBuilding(config);
    },
  });
}

export type { BuildingData, BuildingPayloadSchema };
export {
  buildingPayloadSchema,
  buildingLoader,
  useBuildings,
  useBuilding,
  useBuildingCreate,
  useBuildingEdit,
  useBuildingDelete,
  useBuildingBulkDelete,
  useExportBuilding,
  // Exporting for testing purposes
  exportBuilding,
  waitForMediaCompletion,
};
